import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, {  GridWidths } from "@insite/mobius/GridItem";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { css } from "styled-components";

const enum fields {
    leftSide = "leftSide",
    rightSide = "rightSide",
    gap = "gap"
}

export interface StickySidebarWidgetStyles {
    sideBarContainer?: InjectableCss;
    sideBarGrid?: InjectableCss;
}

export const StickySidebarWidgetStyles: StickySidebarWidgetStyles = {
    sideBarContainer:{
        css: css`
            width: 100%;
            padding-bottom: 15px;
            min-height: 100vh;
        `
    },
    sideBarGrid:{
        css:css`
            position: relative;
        `
    }
};

const styles = StickySidebarWidgetStyles;

interface Props extends WidgetProps {
    fields: {
        [fields.leftSide]: GridWidths[];
        [fields.rightSide]: string;
        [fields.gap]: number;
    };
}

const StickySideBarWidget = ({
    id,
    fields
}: Props) => {

    const mainContentRef = React.useRef<HTMLDivElement | null>(null);
    const sidebarRef = React.useRef<HTMLDivElement | null>(null);
    const contentRef = React.useRef<HTMLDivElement | null>(null);
    const topElementRef = React.useRef<HTMLDivElement | null>(null);
    const lastElementRef = React.useRef<HTMLDivElement | null>(null);
    const hasUserInteractedRef = React.useRef(false);
    const [isMobile, setIsMobile] = React.useState<boolean | null>(null);
    const [mainContentHeight, setMainContentHeight] = React.useState(0);
    const [sidebarContentHeight, setSidebarContentHeight] = React.useState(0);

    const isCms = ()=>{
        let isInShell = false;
        try {
            isInShell =
                !!window &&
                window.parent &&
                window.parent.location.toString().toLowerCase().indexOf("/contentadmin/") > 0;
        } catch (error) {
        }
        return isInShell;
    }

    React.useEffect(() => {
        if (typeof window !== undefined) {
            const handleResize = () => {
                const mobile = window.innerWidth < 767;
                setIsMobile(mobile);
            };

            handleResize();

            window.addEventListener("resize", handleResize);
            return () => window.removeEventListener("resize", handleResize);
        }
    }, []);

    React.useEffect(() => {
        let rafId = 0;

        const measureHeights = () => {
            const mainHeight = mainContentRef.current?.getBoundingClientRect().height ?? 0;
            const sidebarHeight = contentRef.current?.getBoundingClientRect().height ?? 0;

            setMainContentHeight(Math.round(mainHeight));
            setSidebarContentHeight(Math.round(sidebarHeight));
        };

        const scheduleMeasureHeights = () => {
            if (rafId) {
                return;
            }

            rafId = window.requestAnimationFrame(() => {
                rafId = 0;
                measureHeights();
            });
        };

        measureHeights();
        window.addEventListener("resize", measureHeights);
        window.addEventListener("scroll", scheduleMeasureHeights, { passive: true });

        return () => {
            window.removeEventListener("resize", measureHeights);
            window.removeEventListener("scroll", scheduleMeasureHeights);
            if (rafId) {
                window.cancelAnimationFrame(rafId);
            }
        };
    }, []);


    React.useEffect(() => {

        const markUserInteraction = () => {
            hasUserInteractedRef.current = true;
        };

        const removeInteractionListeners = () => {
            window.removeEventListener("wheel", markUserInteraction);
            window.removeEventListener("touchmove", markUserInteraction);
            window.removeEventListener("keydown", markUserInteraction);
        };

        window.addEventListener("wheel", markUserInteraction, { passive: true });
        window.addEventListener("touchmove", markUserInteraction, { passive: true });
        window.addEventListener("keydown", markUserInteraction);

        const hasHubspotModalContent = document.querySelector("#hubspotFormModal.modalContent");
        if (hasHubspotModalContent) {
            const sidebarContent = contentRef.current;
            if (sidebarContent) {
                sidebarContent.style.position = "";
                sidebarContent.style.top = "";
                sidebarContent.style.bottom = "";
                sidebarContent.style.transform = "";
                sidebarContent.style.width = "";
            }
            removeInteractionListeners();
            return;
        } else

        if (!isCms()) {
            if (!isMobile) {
                if (mainContentHeight > 0 && sidebarContentHeight > mainContentHeight) {
                    const sidebarContent = contentRef.current;
                    if (sidebarContent) {
                        sidebarContent.style.position = "";
                        sidebarContent.style.top = "";
                        sidebarContent.style.bottom = "";
                        sidebarContent.style.transform = "";
                        sidebarContent.style.width = "";
                    }

                    removeInteractionListeners();
                    return;
                }

                const handleScroll = () => {
                    const hasModalContent = document.querySelector("#hubspotFormModal.modalContent");

                    if (hasModalContent) {
                        const sidebarContent = contentRef.current;
                        if (sidebarContent) {
                            sidebarContent.style.position = "";
                            sidebarContent.style.top = "";
                            sidebarContent.style.bottom = "";
                            sidebarContent.style.transform = "";
                            sidebarContent.style.width = "";
                        }
                        return;
                    }

                    if (!hasUserInteractedRef.current) {
                        return;
                    }

                    const sidebar = sidebarRef.current;
                    const sidebarContent = contentRef.current;

                    if (!sidebar || !sidebarContent) return;

                    const scrollTop = window.scrollY;
                    const contentHeight = sidebarContent.getBoundingClientRect().height;
                    const viewportHeight = window.innerHeight;

                    const isTopVisible = topElementRef.current && topElementRef.current.getBoundingClientRect().top >= 0 && topElementRef.current.getBoundingClientRect().top <= window.innerHeight;
                    const sidebarTop = sidebar.getBoundingClientRect().top + window.pageYOffset;

                    if(isTopVisible){
                        sidebarContent.style.position = "relative";
                        sidebarContent.style.top = "0px";
                        sidebarContent.style.bottom = "auto";
                        sidebarContent.style.transform = "none";
                        sidebarContent.style.width = `${sidebar.offsetWidth - fields.gap}px`;
                        return;
                    }
                    if (
                        scrollTop >=
                        contentHeight - viewportHeight + sidebarTop
                    ) {
                        if (
                            lastElementRef.current &&
                            lastElementRef.current.getBoundingClientRect().top <= window.innerHeight
                        ) {
                            sidebarContent.style.position = "absolute";
                            sidebarContent.style.top = "auto";
                            sidebarContent.style.bottom = "0px";
                            sidebarContent.style.transform = "none";
                            sidebarContent.style.width = `${sidebar.offsetWidth - fields.gap}px`;
                        } else {
                            sidebarContent.style.position = "fixed";
                            sidebarContent.style.top = "0";
                            sidebarContent.style.bottom = "auto";
                            sidebarContent.style.transform = `translateY(-${contentHeight - viewportHeight}px)`;
                            sidebarContent.style.width = `${sidebar.offsetWidth - fields.gap}px`;
                        }
                    } else {
                        // Reset
                        sidebarContent.style.position = "";
                        sidebarContent.style.top = "";
                        sidebarContent.style.bottom = "";
                        sidebarContent.style.transform = "";
                        sidebarContent.style.width = "";
                    }
                };
                window.addEventListener("scroll", handleScroll);

                return () => {
                    removeInteractionListeners();
                    window.removeEventListener("scroll", handleScroll);
                };
            }
        }

        return () => {
            removeInteractionListeners();
        };
    }, [isMobile, mainContentHeight, sidebarContentHeight]);

    return (
        <GridContainer
            gap={fields.gap || 0}
        >
            {/* Top Marker */}
            <GridItem width={12} style={{padding: "0px"}} ref={topElementRef}></GridItem>

            {/* Main Content */}
            <GridItem
                width={[12, 8, 8, 8, 8]}
                ref={mainContentRef}
                style={{height:"fit-content"}}
            >
                <Zone contentId={id} zoneName="Content" />
            </GridItem>

            {/* Sidebar */}
            <GridItem
                width={[12, 4, 4, 4, 4]}
                ref={sidebarRef}
                {...styles.sideBarGrid}
            >
                <StyledWrapper ref={contentRef} {...styles.sideBarContainer} id="stickySideBarContent" >
                    <Zone contentId={id} zoneName="Content0" />
                </StyledWrapper>
            </GridItem>
            <GridItem width={12} style={{padding: "0px"}} ref={lastElementRef}></GridItem>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: StickySideBarWidget,
    definition: {
        group: "Basic",
        displayName: "Sticky sidebar Conatiner",
        fieldDefinitions: [
            //Below code is for making the width of the column dynamic.
            //  {
            //     name: fields.leftSide,
            //     displayName: "Left side width",
            //     editorTemplate: "ColumnsField",
            //     defaultValue: [12, 8, 8, 8, 8],
            //     fieldType: "General",
            //     sortOrder: 1,
            //     validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            // },
            // {
            //     name: fields.rightSide,
            //     displayName: "Right side width",
            //     editorTemplate: "ColumnsField",
            //     defaultValue: [12, 4, 4, 4, 4],
            //     fieldType: "General",
            //     sortOrder: 1,
            //     validate: value => (value.length > 0 ? null : "Please enter a valid layout."),
            // },
            {
                name: fields.gap,
                displayName: "Gap Between Column",
                editorTemplate: "IntegerField",
                defaultValue: 0,
                fieldType: "General",
                max: 1000,
                min: 0,
                sortOrder: 2,
            },
        ],
    },
};

export default widgetModule;
